<script setup lang="ts">
import { computed, defineEmits, defineProps, watch } from 'vue'
import BaseInputWrapper from './BaseInputWrapper.vue'
import { useCurrencyInput } from 'vue-currency-input'
import { twMerge } from 'tailwind-merge'
import { useFieldHelper } from './useBaseInput'
import { InputSize } from '../../typings'

const props = withDefaults(
    defineProps<{
        modelValue: number
        label?: string
        name: string
        rules?: any
        currency: string
        disabled: boolean
        size: InputSize
        max: number
        placeholder?: string
        classes?: string
    }>(),
    { size: 'md', disabled: false, max: null, placeholder: '0,00' }
)

const inputSizeClasses = computed(() => {
    const config: { [key: InputSize]: string } = {
        xs: 'pr-6 text-xs py-1',
        sm: 'pr-8 text-sm py-1',
        md: 'pr-10 text-md py-2',
        lg: 'text-lg pr-12 text-lg py-2',
        xl: 'text-xl sm:text-3xl py-4',
    }
    return config[props.size]
})

const placeholderSizeClasses = computed(() => {
    const config: { [key: InputSize]: string } = {
        xs: 'sm:text-xs',
        sm: 'sm:text-sm',
        md: 'sm:text-md',
        lg: 'sm:text-lg',
        xl: 'sm:text-xl',
    }
    return config[props.size]
})

const { errorMessage, handleChange } = useFieldHelper(props.name, props.rules, { initialValue: props.modelValue })

const { inputRef, numberValue, setValue } = useCurrencyInput({
    currency: props.currency,
    currencyDisplay: 'hidden',
    locale: 'de-DE',
})

const emit = defineEmits(['update:modelValue'])

function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

watch(numberValue, (val) => {
    if (!isNaN(props.max) && props.max !== null && val > props.max) {
        numberValue.value = props.max
        return
    }
    updateValue(val)
})

watch(
    () => props.max,
    (val) => {
        if (numberValue.value > val) {
            numberValue.value = val
            return
        }
    }
)
const errorMessageClasses = computed(() => {
    return errorMessage.value
        ? 'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : ''
})

const disabledClasses = computed(() => {
    return props.disabled ? 'bg-gray-50 border-gray-200' : ''
})

watch(
    () => props.modelValue,
    (val: number) => setValue(val)
)
</script>

<template>
    <BaseInputWrapper :name="props.name" :label="props.label">
        <div class="relative rounded-md shadow-sm">
            <input
                :id="props.name"
                v-bind="$attrs"
                ref="inputRef"
                type="text"
                :disabled="disabled"
                :name="props.name"
                min="0"
                :aria-invalid="!!errorMessage"
                :class="
                    twMerge(
                        'block w-full rounded-md border-gray-300 pl-6 text-left text-gray-600 ring-transparent focus:border focus:border-primary focus:ring-primary',
                        inputSizeClasses,
                        disabledClasses,
                        errorMessageClasses,
                        classes
                    )
                "
                :placeholder="placeholder"
                aria-describedby="price-currency"
                autocomplete="off"
                @click="$event.target.select()" />
            <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center"
                :class="errorMessage ? 'pr-16' : 'pr-6'">
                <span id="price-currency" :class="twMerge('text-gray-500', placeholderSizeClasses)">
                    {{ props.currency }}
                </span>
            </div>
        </div>
    </BaseInputWrapper>
</template>
